import { backendApi } from "./backendApi.rtkq";
import type { AgentTuningRequest, AgentTuningResponse } from "@/types/agentTuning.types";

export const agentTuningApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    createAgentTuning: builder.mutation<AgentTuningResponse, AgentTuningRequest>({
      queryFn: async (
        { files, systemInstructions, systemInstructionsVersion, content, contentVersion, responseSchema, responseSchemaVersion },
        _queryApi,
        _extraOptions,
        baseQuery
      ) => {
        try {
          // Convert File objects to base64 format
          const filesAsBase64 = await Promise.all(
            files.map(async (file) => {
              const arrayBuffer = await file.arrayBuffer();
              const bytes = new Uint8Array(arrayBuffer);
              let binary = "";
              for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
              }
              const base64 = btoa(binary);
              return {
                name: file.name,
                type: file.type,
                base64: `data:${file.type};base64,${base64}`,
              };
            })
          );

          // Create agent tuning
          const createResult = await baseQuery({
            url: "/agent-tuning",
            method: "POST",
            body: {
              files: filesAsBase64,
              systemInstructions,
              systemInstructionsVersion,
              content,
              contentVersion,
              responseSchema,
              responseSchemaVersion,
            },
          });

          if (createResult.error) {
            return { error: createResult.error as any };
          }

          const createData = createResult.data as any;
          if (!createData?.tuningId) {
            return { error: { message: "Failed to create agent tuning" } };
          }

          // Fetch the full status after creation
          const statusResult = await baseQuery({
            url: `/agent-tuning/${createData.tuningId}`,
            method: "GET",
          });

          if (statusResult.error) {
            return { error: statusResult.error as any };
          }

          const statusData = statusResult.data as AgentTuningResponse;
          return { data: statusData };
        } catch (error: unknown) {
          console.error("Error creating agent tuning:", error);
          return { error: { message: (error as Error).message } };
        }
      },
      invalidatesTags: ["AgentTuning"],
    }),

    getAgentTuningStatus: builder.query<AgentTuningResponse, string>({
      query: (tuningId) => `/agent-tuning/${tuningId}`,
      providesTags: ["AgentTuning"],
    }),
  }),
});

export const { useCreateAgentTuningMutation, useGetAgentTuningStatusQuery } = agentTuningApi;
